import { Box, Flex } from '@common/fundamentals'
import {
  Button,
  TextInput,
  MenuItem,
  IconButton,
  LoadingButton,
} from '@common/material'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import { useUpdateSiteBoxConfiguration } from '@core/react-query/features/siteBoxDocuments/hooks/useUpdateSiteBoxConfiguration'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { useProcessSiteBoxConfiguration } from '@core/react-query/features/siteBoxDocuments/hooks/useProcessSiteBoxConfiguration'
import { useToast } from '@core/toast'
import {
  ISiteBoxDocumentConfigConfig,
  SiteBoxDocumentConfig,
} from '@core/entities/SiteBoxDocumentConfig'
import DeleteIcon from '@mui/icons-material/Delete'
import { localizedStrings } from '@core/strings'
import { useGetSiteBoxConfiguration } from '@core/react-query/features/siteBoxDocuments/hooks/useGetSiteBoxConfiguration'
import { useEffect } from 'react'
import {
  ArrowBack,
  IosShareOutlined,
  SystemUpdateAltOutlined,
} from '@mui/icons-material'
import { TemplateQuestionBuilderImportQuestionsModal } from '@modules/templates/templateBuilder/questionBuilder/TemplateQuestionBuilderImportQuestionsModal'
import { useModal } from '@common/GlobalModal'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import { useClipboard } from 'webapp/providers/useClipboard'

type SiteBoxDocumentDrawerFormProps = {
  documentId: number
  organisationId: number
  projectId: number
  onButtonClick: () => void
}

export const SiteBoxDocumentDrawerForm: React.FC<
  SiteBoxDocumentDrawerFormProps
> = ({ documentId, organisationId, projectId, onButtonClick }) => {
  const updateSiteBoxConfiguration = useUpdateSiteBoxConfiguration()
  const processSiteBoxConfiguration = useProcessSiteBoxConfiguration()
  const toast = useToast()
  const { data: profile } = useGetProfile()
  const { showModal, hideModal } = useModal()
  const { flags } = useFeatureFlags(['beta'])
  const { copyToClipboard } = useClipboard()

  const { data: siteBoxConfiguration } = useGetSiteBoxConfiguration({
    organisationId,
    projectId,
    id: documentId,
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<ISiteBoxDocumentConfigConfig>({
    defaultValues: {
      type: 'RAMS',
      subCategory: 'string',
      processType: 'string',
      queries: [
        {
          id: uuidv4(),
          query: '',
          returnType: 'SUMMARY',
        },
      ],
    },
  })

  const {
    fields: queries,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'queries',
  })

  const onSubmit = (data: ISiteBoxDocumentConfigConfig) => {
    const dataToSubmit = SiteBoxDocumentConfig.removeEmptyQueries(data)

    if (dataToSubmit.queries.length > 0) {
      updateSiteBoxConfiguration.mutate(
        {
          organisationId,
          projectId,
          documentId,
          config: { ...dataToSubmit, type: 'RAMS' },
          userId: profile?.id,
          modifiedBy: profile?.id,
          createdBy: profile?.id,
        },
        {
          onSuccess: () => {
            processSiteBoxConfiguration.mutate({
              documentId,
            })
            toast.success(localizedStrings.siteBoxConfigurationUpdated)
            onButtonClick()
          },
          onError: (error) => {
            toast.error(error.message)
          },
        },
      )
    }
  }

  useEffect(() => {
    reset({
      queries: [
        ...(siteBoxConfiguration?.config.queries || [
          { id: uuidv4(), query: '', returnType: 'SUMMARY' },
        ]),
      ],
    })
  }, [siteBoxConfiguration, reset])

  return (
    <Box>
      <Flex justifyContent='space-between' alignItems='center' mb={2}>
        <IconButton onClick={onButtonClick} aria-label='Go Back'>
          <ArrowBack />
        </IconButton>
        {flags.beta && (
          <Flex gap={1}>
            <Button
              startIcon={<IosShareOutlined />}
              onClick={() => {
                const currentConfig = watch()
                copyToClipboard(JSON.stringify(currentConfig))
              }}
            >
              {localizedStrings.exportQuestions}
            </Button>
            <Button
              startIcon={<SystemUpdateAltOutlined />}
              onClick={() => {
                showModal(
                  <TemplateQuestionBuilderImportQuestionsModal
                    onImport={(configJson: string) => {
                      const importedConfig = JSON.parse(configJson)
                      setValue('queries', importedConfig.queries)
                      hideModal()
                    }}
                  />,
                )
              }}
            >
              {localizedStrings.importQuestions}
            </Button>
          </Flex>
        )}
      </Flex>
      {queries.map((query, queryIndex) => (
        <Flex key={query.id} flexDirection='column' gap={1} mt={2}>
          <Flex
            justifyContent='space-between'
            gap={1}
            sx={{ alignItems: 'flex-start' }}
          >
            <Controller
              control={control}
              name={`queries.${queryIndex}.query`}
              render={({ field }) => (
                <TextInput
                  sx={{ width: '100%', flex: 3 }}
                  size='small'
                  value={field.value}
                  onChange={field.onChange}
                  label={localizedStrings.query}
                />
              )}
            />
            <Controller
              control={control}
              name={`queries.${queryIndex}.returnType`}
              render={({ field: { onChange, value, ...field } }) => (
                <TextInput
                  sx={{ width: '100%', flex: 1 }}
                  size='small'
                  select
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  label={localizedStrings.returnType}
                  {...field}
                >
                  <MenuItem value='SUMMARY'>
                    {localizedStrings.summary}
                  </MenuItem>
                  <MenuItem value='LIST'>{localizedStrings.list}</MenuItem>
                  <MenuItem value='DATE'>{localizedStrings.date}</MenuItem>
                  <MenuItem value='NUMBER'>{localizedStrings.number}</MenuItem>
                  <MenuItem value='BOOLEAN'>
                    {localizedStrings.boolean}
                  </MenuItem>
                </TextInput>
              )}
            />
            <IconButton color='error' onClick={() => remove(queryIndex)}>
              <DeleteIcon />
            </IconButton>
          </Flex>
        </Flex>
      ))}
      <Button
        sx={{ alignSelf: 'flex-start', mt: 2 }}
        onClick={() =>
          append({ id: uuidv4(), query: '', returnType: 'SUMMARY' })
        }
      >
        {localizedStrings.addQuery}
      </Button>
      <Flex gap={2} mt={2} justifyContent='flex-end'>
        <Button variant='outlined' onClick={onButtonClick}>
          {localizedStrings.cancel}
        </Button>
        <LoadingButton
          loading={updateSiteBoxConfiguration.isLoading}
          disabled={!isDirty}
          variant='contained'
          onClick={handleSubmit(onSubmit)}
        >
          {localizedStrings.save}
        </LoadingButton>
      </Flex>
    </Box>
  )
}
